/**
 * Which Vera module an activity event belongs to.
 *
 * Derived from MODULE_CATALOG rather than hand-listed, because the hand-listed
 * version is what broke: telemetry knew sales/ops/catalog while the platform
 * grew to fifteen modules, so Rx, Academy, Portfolio, Maple, Finance, Ideas,
 * Share, Proposal and Marketing all recorded as `vera-shell` and the admin
 * page's app filter could not find them (ADO #1558). Reading the catalog means
 * the next module is attributed the day it ships, with no second list to update.
 *
 * The catalog keeps a module's `id` and its route apart on purpose (Proposal
 * is `rfp` mounted at `/proposal`), so this maps prefix -> id rather than
 * assuming they match.
 */

import { MODULE_CATALOG } from '../../../ui/components/moduleCatalogData';

/** Events that belong to no module: the launcher, and anything above a module. */
export const SHELL_APP_ID = 'vera-shell';

/**
 * Mounted modules, longest route first so `/share` can never swallow a longer
 * sibling. `status: 'soon'` entries are skipped: their path is '#', not a route.
 */
const MOUNTED = MODULE_CATALOG.filter((m) => m.path.startsWith('/'))
  .map((m) => ({ prefix: m.path.replace(/\/+$/, ''), id: m.id, name: m.name }))
  .sort((a, b) => b.prefix.length - a.prefix.length);

/** Every app id an event can carry, in launcher order, shell last. */
export const ACTIVITY_APP_IDS: readonly string[] = [
  ...MODULE_CATALOG.filter((m) => m.path.startsWith('/')).map((m) => m.id),
  SHELL_APP_ID,
];

/** Display label for an app id, for filters and badges. */
export function appLabel(id: string): string {
  if (id === SHELL_APP_ID) return 'Shell (launcher)';
  return MOUNTED.find((m) => m.id === id)?.name ?? id;
}

/**
 * The module a route belongs to. Matches on whole path segments so `/shared`
 * is not read as `/share`. Falls back to the shell for `/` and anything that
 * is not under a module.
 */
export function appIdForPath(path: string): string {
  const clean = path.replace(/\/+$/, '') || '/';
  for (const m of MOUNTED) {
    if (clean === m.prefix || clean.startsWith(m.prefix + '/')) return m.id;
  }
  return SHELL_APP_ID;
}

/**
 * The mount point of the module a route belongs to ('/ops/groups/1' -> '/ops'),
 * or '/' at the launcher. Used to tell a module's landing page apart from a
 * page inside it.
 */
export function moduleBaseForPath(path: string): string {
  const segment = path.split('/')[1];
  return segment ? `/${segment}` : '/';
}

/** 'group-site-provisioning' -> 'Group Site Provisioning'. */
function titleCase(segment: string): string {
  return segment
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * A readable area for a route with no matching nav item. This is the fallback
 * that replaces the literal string 'Unknown', which is what the admin page was
 * full of: a route the module's nav does not name is still a place someone
 * went, and the path already says where.
 *
 * A module's landing page reports the module ('/rx' -> 'Rx'); anything deeper
 * reports its first segment ('/ops/guide/foo' -> 'Guide'); the launcher is
 * 'Home'.
 */
export function areaFromPath(path: string): string {
  const clean = path.replace(/\/+$/, '') || '/';
  const base = moduleBaseForPath(clean);
  const sub = clean.slice(base.length).split('/').filter(Boolean)[0];
  if (sub) return titleCase(sub).slice(0, 100);
  if (base === '/') return 'Home';
  return MOUNTED.find((m) => m.prefix === base)?.name ?? titleCase(base.slice(1));
}
